import styled, { css } from 'styled-components';
import { hideVisually, rgba as polishedRgba } from 'polished';

import { defaultThemeProp } from '../../../theme';
import { StyledSelect } from '../../Select/Select';
import { StyledButton } from '../../Button';
import { tryCatch } from '../../../utils';

export const StyledPick = styled(StyledButton)(props => {
  const {
    theme: {
      base: {
        colors,
        'border-radius': borderRadius,
        palette: { interactive, light },
        'disabled-opacity': disabledOpacity,
        spacing
      }
    }
  } = props;

  return css`
    height: 2.625rem;
    border-radius: calc(0.5 * ${borderRadius});
    padding: 0 ${spacing};
    background-color: transparent;

    & + & {
      margin-inline-start: 0;
    }

    &[aria-readonly]:enabled {
      opacity: ${disabledOpacity};
    }

    &[aria-selected]:enabled {
      background-color: ${interactive};
      color: ${light};
    }

    &:disabled {
      background-color: ${colors.gray['extra-light']};
      border-radius: 0;
    }

    &:focus {
      outline: none;
      box-shadow: inset 0 0 0 0.125rem ${colors.blue.light};
    }
  `;
});

StyledPick.defaultProps = defaultThemeProp;

export const StyledMonthPicker = styled.div(props => {
  const {
    theme: {
      base: {
        spacing,
        'border-radius': baseBorderRadius,
        palette: { 'foreground-color': color }
      },
      components: { 'form-control': formControl }
    }
  } = props;
  return css`
    margin: calc(2 * ${spacing}) calc(0.5 * ${spacing});
    font-size: 0.875rem;

    ${StyledSelect} {
      border-radius: calc(${baseBorderRadius} * ${formControl['border-radius']});
    }

    ${StyledButton} {
      border-radius: calc(${baseBorderRadius} * ${formControl['border-radius']});
      border: 0;
      &:enabled {
        color: ${color};
      }
    }
  `;
});

StyledMonthPicker.defaultProps = defaultThemeProp;

export const StyledDatePicker = styled.div(props => {
  const {
    theme: {
      base: { spacing },
      components: {
        'form-control': { 'foreground-color': foreground }
      }
    }
  } = props;
  return css`
    width: max-content;

    ${StyledButton} {
      border: transparent;
      color: ${foreground};
    }
    padding: 0 ${spacing} ${spacing} ${spacing};
  `;
});

StyledDatePicker.defaultProps = defaultThemeProp;

export const StyledColumnLabel = styled.span`
  text-align: center;
  margin: 0 auto;
  font-size: 0.75rem;
`;

StyledColumnLabel.defaultProps = defaultThemeProp;

export const ScreenReaderOnly = styled.div`
  ${hideVisually}
`;

export const StyledDay = styled(StyledPick)<{ current: boolean; highlighted: boolean }>(props => {
  const {
    theme: {
      base: {
        colors,
        palette: { interactive }
      }
    },
    current,
    highlighted
  } = props;

  const highlightedBgColor = tryCatch(() => polishedRgba(interactive, 0.1));

  return css`
    background-color: ${highlighted ? highlightedBgColor : 'transparent'};

    ${highlighted &&
    css`
      border-radius: 0;
    `}

    ${current &&
    css`
      color: ${colors.slate.dark};
      font-weight: bold;
    `}
  `;
});

StyledDay.defaultProps = defaultThemeProp;

export const StyledWeekLabel = styled(StyledColumnLabel)(props => {
  const {
    theme: {
      base: { spacing, breakpoints }
    }
  } = props;

  return css`
    margin-inline-end: 0;

    @media (min-width: ${breakpoints.sm}) {
      margin-inline-end: ${spacing};
    }
  `;
});

StyledWeekLabel.defaultProps = defaultThemeProp;

export const StyledWeekNumber = styled.span(props => {
  const {
    theme: {
      base: { breakpoints, spacing, colors, 'disabled-opacity': disabledOpacity }
    }
  } = props;

  return css`
    line-height: 2.625rem;
    padding: 0;
    margin-inline-end: 0;
    background-color: ${colors.gray['extra-light']};
    display: inline-block;
    opacity: ${disabledOpacity};

    @media (min-width: ${breakpoints.sm}) {
      padding: 0 ${spacing};
      margin-inline-end: ${spacing};
    }
  `;
});

StyledWeekNumber.defaultProps = defaultThemeProp;

export const StyledWeekGrid = styled.table(props => {
  const {
    theme: {
      base: { spacing }
    }
  } = props;
  return css`
    table-layout: fixed;
    border-collapse: collapse;
    border-spacing: 0;

    thead {
      th {
        padding-bottom: ${spacing};
      }
    }
    ${StyledPick} {
      margin-inline-end: ${spacing};
    }
  `;
});

StyledWeekGrid.defaultProps = defaultThemeProp;

export const StyledCalendar = styled.table(props => {
  const {
    theme: {
      base: { 'border-radius': borderRadius, spacing }
    }
  } = props;
  return css`
    table-layout: fixed;
    border-collapse: collapse;
    border-spacing: 0;

    th {
      max-width: 2.625rem;
    }

    thead {
      th {
        padding-bottom: ${spacing};
      }
    }

    td {
      width: 2.625rem;
      max-width: 2.625rem;

      ${StyledDay} {
        width: 100%;
      }
    }

    tr {
      &:first-child {
        ${StyledWeekNumber} {
          border-radius: calc(0.5 * ${borderRadius}) calc(0.5 * ${borderRadius}) 0 0;
        }
      }
      &:last-child {
        ${StyledWeekNumber} {
          border-radius: 0 0 calc(0.5 * ${borderRadius}) calc(0.5 * ${borderRadius});
        }
      }
    }
  `;
});

StyledCalendar.defaultProps = defaultThemeProp;
